import { NO_IMG } from "@/_constant";
import { useLanguageContext } from "@/hooks";
import type { DashboardContentProps } from "./types";
import { Button } from "@/_component/Common";
import { hasCreatePostCard, hasKnowMoreCard, isDashboardPlanExpired } from "./dashboard-profile-card.resolver";
import ProfileCardKpis from "./ProfileCardKpis";
import { KnowMore, RenewCard } from "@/_component/shared";

const ProfileCardContent = ({ profile, config, recentPostThumbnail, recentPostCount = 0, executeAction }: DashboardContentProps) => {
  const { language } = useLanguageContext();
  const isPlanExpired = isDashboardPlanExpired(profile, config);

  const resolveTitle = () => {
    if (profile.accessName === 'dealer' && profile.categoryName === 'Property') {
      return language("label.create_post_to_sell_your_product") || 'Create post to sell your product';
    }

    if (profile.accessName === "service-provider") {
      return language("create_post_to_give_your_services") || 'Create post to give your services';
    }

    return language(config.createPost.titleKey) || config.createPost?.title;
  }

  const title = resolveTitle();

  const hasCreatePost = hasCreatePostCard(config);
  const hasKnowMore = hasKnowMoreCard(config);

  return (
    <div className="row g-3 align-items-stretch">

      {(hasCreatePost || isPlanExpired) && (
        <div className="col-lg-6 col-xl-7">
          <div className="bg-white rounded-4 p-3 h-100 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 shadow-sm border">
            <div className="flex-fill text-center text-md-start ms-md-2">
              <h5 className="fw-bold mb-3">{title}</h5>
              {isPlanExpired ? (
                <Button warning solid rounded small onClick={() => executeAction(config.renewAction)}>
                  <i className="bi bi-arrow-repeat me-2" />{language("label.renew_plan") || "Renew Plan"}
                </Button>
              ) : (
                <Button primary solid rounded small onClick={() => executeAction(config.createPost.action)}>
                  <i className="bi bi-plus-circle me-2" />{language(config.createPost.buttonLabelKey) || "Create New Post"}
                </Button>
              )}
            </div>
            <div role="button" className="shadow rounded-4 mt-3 mt-md-0 border-0 bg-transparent" onClick={() => executeAction(config.postingsAction)} style={{ height: 90, width: 160 }}>
              <div className="position-relative text-center w-100 h-100">
                <img src={recentPostThumbnail || NO_IMG} className="img-fluid h-100 w-100 object-fit-cover" alt="Recent post" />
                <div className="p-1 kg-bg-black text-white position-absolute bottom-0 w-100 rounded-bottom-4 fs-7 opacity-75">
                  <small>View posts ({recentPostCount})</small> <i className="bi bi-chevron-right ms-1" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {(hasKnowMore) && (
        <div className="col-lg-6 col-lg-8">
          {isPlanExpired && (
            <RenewCard config={config} executeAction={executeAction} />
          )}
          {!isPlanExpired && (
            <KnowMore config={config} executeAction={executeAction} />
          )}
        </div>
      )}

      <ProfileCardKpis
        profile={profile}
        kpis={config.kpis}
        className={(hasKnowMore && !hasCreatePost) ? 'col-lg-6 col-xl-4' : 'col-lg-6 col-xl-5'}
        hasPrimaryContent={hasCreatePost || isPlanExpired}
        executeAction={executeAction}
      />
    </div>
  );
};

export default ProfileCardContent;
